import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface EnterpriseViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const EnterpriseView = ({ isOpen, onClose }: EnterpriseViewProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const sapCoreModules = [
    // { name: "SAP S/4HANA", path: "/sap-s4hana" },
    // { name: "Rise with SAP", path: "/rise-with-sap" },
    { name: "SAP S/4HANA & RISE", path: "/sap-s4hana-rise" },
    // { name: "SAP FI (Financial Accounting)", path: "/sap/fi" },
    // { name: "SAP CO (Controlling)", path: "/sap/co" },
    { name: "SAP Finance & Control", path: "/sap/fi-co" },
    // { name: "SAP MM (Materials Management)", path: "/sap/mm" },
    // { name: "SAP PP (Production Planning)", path: "/sap/pp" },
    // { name: "SAP SD (Sales & Distribution)", path: "/sap/sd" },
    { name: "SAP Supply Chain & Manufacturing", path: "/sap/scm" },
    // { name: "SAP HR/HCM", path: "/sap/hr" },
    { name: "SAP HCM & Success Factor", path: "/sap/hcm" },
    { name: "SAP Analytics & Reporting", path: "/sap/bi-bw" },
    { name: "SAP CRM", path: "/sap/crm" },
    // { name: "SAP BASIS", path: "/sap/basis" },
    // { name: "SAP Security", path: "/sap/security" },
    { name: "SAP Technical Services", path: "/sap/technical" },
  ];

  const sapEndToEndSolutions = [
    { name: "SAP End-to-End Solutions", path: "/sap-end-to-end" },
  ];

  const workdayModules = [
    // { name: "Workday HCM Core", path: "/workday/hcm-core" },
    // { name: "Workday Payroll", path: "/workday/payroll" },
    { name: "Workday Core HCM & Payroll", path: "/workday/hcm-payroll" },
    // { name: "Workday Talent Management", path: "/workday/talent" },
    { name: "Workday Talent & Recruiting", path: "/workday/talent-recruiting" },
    { name: "Workday Learning & Development", path: "/workday/learning" },
    // { name: "Workday Recruiting", path: "/workday/recruiting" },
    // { name: "Workday Time Tracking", path: "/workday/time" },
    { name: "Workday Time Tracking & Absence", path: "/workday/time-absence" },
    // { name: "Workday Benefits", path: "/workday/benefits" },
    // { name: "Workday Compensation", path: "/workday/compensation" },
    {
      name: "Workday Compensation & Benefits",
      path: "/workday/compensation-benefits",
    },
    // { name: "Workday Absence Management", path: "/workday/absence" },
  ];

  const endToEnd = [
    { name: "Consulting & Roadmap Strategy", path: "/end-to-end/c&rs" },
    { name: "Implementation & Migration", path: "/end-to-end/i&m" },
    { name: "Integration & Customization", path: "/end-to-end/i&c" },
    { name: "Managed Services & Support", path: "/end-to-end/mss" },
  ];

  const salesforceModules = [
    { name: "Sales Cloud", path: "/salesforce/sales-cloud" },
    { name: "Service Cloud", path: "/salesforce/service-cloud" },
    { name: "Marketing Cloud", path: "/salesforce/marketing-cloud" },
    { name: "Commerce Cloud", path: "/salesforce/commerce-cloud" },
    {
      name: "Experience (Community) Cloud",
      path: "/salesforce/community-cloud",
    },
    // { name: "Analytics Cloud", path: "/salesforce/analytics-cloud" },
    {
      name: "Analytics & Data Cloud",
      path: "/salesforce/analytics-data-cloud",
    },
    { name: "App Cloud (Platform)", path: "/salesforce/app-cloud" },
    // { name: "Data Cloud", path: "/salesforce/data-cloud" },
  ];

  const integrationAndAutomation = [{ name: "MuleSoft", path: "/mulesoft" }];

  const otherSolutions = [
    { name: "Microsoft Dynamics 365", path: "/microsoft-dynamics-detail" },
    { name: "UKG Kronos", path: "/ukg-detail" },
    { name: "Kinaxis", path: "/kinaxis" },
  ];
  const cloudDataANdAnalytics = [
    { name: "Cloud Platforms", path: "/cloud-platforms-detail" },
    { name: "Analytics", path: "/analytics-detail" },
  ];

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background border-b border-border shadow-2xl z-[55] animate-fade-in"
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-foreground">
            Enterprise Solutions
          </h3>
        </div>

        {/* Mobile Layout */}
        <div className="block lg:hidden">
          <div className="grid grid-cols-1 gap-6 max-h-[70vh] overflow-y-auto pb-4">
            {/* SAP End to End Solutions */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-primary uppercase tracking-wider px-2">
                SAP End to End Solutions
              </h4>
              <div className="space-y-1">
                {sapEndToEndSolutions.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            {/* SAP Modules */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider px-2">
                SAP Modules
              </h4>
              <div className="space-y-1">
                {sapCoreModules.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Workday Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-primary uppercase tracking-wider px-2">
                Workday Solutions
              </h4>
              <div className="space-y-1">
                {workdayModules.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            {/* End-to-End Enterprise Services Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider px-2">
                End-to-End Enterprise Services
              </h4>
              <div className="space-y-1">
                {endToEnd.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Salesforce Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-brand uppercase tracking-wider px-2">
                Salesforce Solutions
              </h4>
              <div className="space-y-1">
                {salesforceModules.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Other Solutions */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-brand uppercase tracking-wider px-2">
                Integration & Automation
              </h4>
              <div className="space-y-1">
                {integrationAndAutomation.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider px-2">
                Other Enterprise Solutions
              </h4>
              <div className="space-y-1">
                {otherSolutions.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-bold text-brand uppercase tracking-wider px-2">
                Cloud, Data & Analytics
              </h4>
              <div className="space-y-1">
                {cloudDataANdAnalytics.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full group text-left px-4 py-3 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-8">
          {/* SAP Modules Column */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 px-2">
              SAP Modules
            </h4>
            <div className="space-y-1">
              {sapCoreModules.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* SAP End to End Solutions in same column */}
            <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4 px-2 mt-6">
              End to End Solutions
            </h4>
            <div className="space-y-1">
              {sapEndToEndSolutions.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Workday Column */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-4 px-2">
              Workday Solutions
            </h4>
            <div className="space-y-1">
              {workdayModules.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* End-to-End Enterprise Services Column */}
            <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4 px-2">
              End-to-End Enterprise Services
            </h4>
            <div className="space-y-1">
              {endToEnd.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Salesforce Column */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-4 px-2">
              Salesforce Solutions
            </h4>
            <div className="space-y-1">
              {salesforceModules.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Other Solutions Column */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-4 px-2">
              Integration & Automation
            </h4>
            <div className="space-y-1">
              {integrationAndAutomation.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
            <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4 px-2 mt-6">
              Other Solutions
            </h4>
            <div className="space-y-1">
              {otherSolutions.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
            <h4 className="text-xs font-bold text-brand uppercase tracking-wider mb-4 px-2 mt-6">
              Cloud, Data & Analytics
            </h4>
            <div className="space-y-1">
              {cloudDataANdAnalytics.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full group text-left px-4 py-2.5 text-sm font-medium text-foreground ${index % 2 === 0 ? "hover:text-primary hover:bg-primary/5" : "hover:text-secondary hover:bg-secondary/5"} transition-all duration-200 rounded-lg`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseView;
